import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Loader2, AlertCircle, CheckCircle, RefreshCw, HelpCircle, ExternalLink } from 'lucide-react'
import { z } from 'zod'

interface UserWithBillingIssue {
  user_id: string
  email: string
  name: string
  stripe_customer_id: string | null
  last_synced_at: string | null
  subscription_count: number
  has_error: boolean
  subscription_status?: string
  product_ids?: string[]
}

const subscriptionSchema = z.object({
  subscriptionId: z.string().trim().min(1, 'Subscription ID is required').startsWith('sub_', 'Must be a valid Stripe subscription ID')
})

export function SubscriptionManagement() {
  const [users, setUsers] = useState<UserWithBillingIssue[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserWithBillingIssue[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'no-customer' | 'no-subs' | 'errors'>('no-subs')
  const [manualSubIds, setManualSubIds] = useState<Record<string, string>>({})
  const [syncHelpOpen, setSyncHelpOpen] = useState(false)
  const [manualSyncHelpOpen, setManualSyncHelpOpen] = useState(false)

  useEffect(() => {
    fetchUsersWithIssues()
  }, [])

  useEffect(() => {
    filterUsers()
  }, [users, searchQuery, filterType])

  const fetchUsersWithIssues = async () => {
    setLoading(true)
    try {
      // Get all users with their billing sync status
      const { data: billingData, error } = await supabase
        .from('user_billing_sync')
        .select(`
          user_id,
          stripe_customer_id,
          last_synced_at
        `)

      if (error) throw error

      // Get subscription counts for each user
      const { data: subsData } = await supabase
        .from('user_subscriptions')
        .select('user_id, product_id, status')

      // Create maps for subscription info
      const subCounts = new Map<string, number>()
      const subStatus = new Map<string, string>()
      const subProducts = new Map<string, string[]>()
      
      subsData?.forEach(sub => {
        subCounts.set(sub.user_id, (subCounts.get(sub.user_id) || 0) + 1)
        subStatus.set(sub.user_id, sub.status)
        
        const products = subProducts.get(sub.user_id) || []
        products.push(sub.product_id)
        subProducts.set(sub.user_id, products)
      })

      // Get user details from auth
      const userPromises = billingData?.map(async (billing) => {
        const { data: { user } } = await supabase.auth.admin.getUserById(billing.user_id)
        
        return {
          user_id: billing.user_id,
          email: user?.email || 'Unknown',
          name: user?.user_metadata?.name || user?.user_metadata?.full_name || 'Unknown',
          stripe_customer_id: billing.stripe_customer_id,
          last_synced_at: billing.last_synced_at,
          subscription_count: subCounts.get(billing.user_id) || 0,
          has_error: false,
          subscription_status: subStatus.get(billing.user_id),
          product_ids: subProducts.get(billing.user_id)
        }
      }) || []

      const usersWithDetails = await Promise.all(userPromises)
      setUsers(usersWithDetails)
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Failed to load users with billing issues')
    } finally {
      setLoading(false)
    }
  }

  const filterUsers = () => {
    let filtered = users

    // Apply filter type
    switch (filterType) {
      case 'no-customer':
        filtered = filtered.filter(u => !u.stripe_customer_id)
        break
      case 'no-subs':
        filtered = filtered.filter(u => u.subscription_count === 0)
        break
      case 'errors':
        filtered = filtered.filter(u => u.has_error)
        break
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(u => 
        u.email.toLowerCase().includes(query) ||
        u.name.toLowerCase().includes(query) ||
        u.user_id.toLowerCase().includes(query)
      )
    }

    setFilteredUsers(filtered)
  }

  const handleManualSync = async (userId: string) => {
    const subscriptionId = manualSubIds[userId]
    
    // Validate subscription ID
    const validation = subscriptionSchema.safeParse({ subscriptionId })
    if (!validation.success) {
      toast.error(validation.error.errors[0].message)
      return
    }

    setSyncing(userId)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      // Call edge function to sync manual subscription
      const response = await fetch(
        `https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1/manual-subscription-sync`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            subscriptionId: validation.data.subscriptionId
          })
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to sync subscription')
      }

      const result = await response.json()
      
      toast.success(`Successfully synced subscription for ${result.email}`)
      
      // Clear the input and refresh the list
      setManualSubIds(prev => ({ ...prev, [userId]: '' }))
      await fetchUsersWithIssues()
    } catch (error: any) {
      console.error('Error syncing subscription:', error)
      toast.error(error.message || 'Failed to sync subscription')
    } finally {
      setSyncing(null)
    }
  }

  const handleRegularSync = async (userId: string) => {
    setSyncing(userId)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(
        `https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1/sync-billing?userId=${userId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to sync billing')
      }

      toast.success('Successfully synced billing data')
      await fetchUsersWithIssues()
    } catch (error: any) {
      console.error('Error syncing:', error)
      toast.error(error.message || 'Failed to sync billing')
    } finally {
      setSyncing(null)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Dialog open={syncHelpOpen} onOpenChange={setSyncHelpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              What Does "Sync" Do?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="space-y-4 text-base">
            <div>
              <h4 className="font-semibold text-foreground mb-2">In Simple Terms:</h4>
              <p>
                The "Sync" button connects to Stripe using the user's existing customer ID and downloads their current subscription data.
                It updates your app's database with their latest subscription information.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">Why Use This?</h4>
              <p>
                Use this when a user already has a Stripe customer ID but their subscription data is missing or outdated in your app.
                This is the standard sync that assumes everything is properly linked.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">When Should I Use It?</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>User has a Stripe customer ID but shows 0 subscriptions</li>
                <li>Subscription data looks outdated</li>
                <li>After making changes in Stripe for this customer</li>
              </ul>
            </div>
            
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2">The Process:</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Uses the existing Stripe customer ID from your database</li>
                <li>Fetches all subscriptions for that customer from Stripe</li>
                <li>Updates your app's subscription records</li>
                <li>Refreshes the list to show updated data</li>
              </ol>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>

      <Dialog open={manualSyncHelpOpen} onOpenChange={setManualSyncHelpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              What Does "Manual Sync" Do?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="space-y-4 text-base">
            <div>
              <h4 className="font-semibold text-foreground mb-2">In Simple Terms:</h4>
              <p>
                Manual Sync lets you directly enter a Stripe subscription ID to manually link it to a user. 
                This is useful when automatic syncing didn't work or when the subscription was created outside your app.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">Why Use This Instead of Regular Sync?</h4>
              <p>
                Use Manual Sync when:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>The user doesn't have a customer ID linked yet</li>
                <li>You created a subscription directly in Stripe</li>
                <li>Regular sync isn't finding the subscription</li>
                <li>You need to manually connect a specific subscription to a user</li>
              </ul>
            </div>
            
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2">How To Use It:</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Find the subscription ID in Stripe (starts with "sub_")</li>
                <li>Paste it in the "Manual Subscription ID" field for the user</li>
                <li>Click "Manual Sync"</li>
                <li>The system will fetch the subscription from Stripe and link it to the user</li>
              </ol>
            </div>
            
            <div className="border-l-4 border-primary pl-4">
              <p className="text-sm">
                <strong>Important:</strong> The subscription ID must start with "sub_" and exist in your Stripe account.
                Manual Sync will also automatically link the Stripe customer to the user if not already linked.
              </p>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>

    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Subscription Management
        </CardTitle>
        <CardDescription>
          Manage users with billing or subscription issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <Input
            placeholder="Search by email, name, or user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="md:max-w-sm"
          />
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('all')}
            >
              All Users ({users.length})
            </Button>
            <Button
              variant={filterType === 'no-customer' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('no-customer')}
            >
              No Stripe Customer ({users.filter(u => !u.stripe_customer_id).length})
            </Button>
            <Button
              variant={filterType === 'no-subs' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('no-subs')}
            >
              No Subscriptions ({users.filter(u => u.subscription_count === 0).length})
            </Button>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchUsersWithIssues}
            className="ml-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Alert */}
        {filteredUsers.length > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Found {filteredUsers.length} user(s) matching your filter criteria.
            </AlertDescription>
          </Alert>
        )}

        {/* Users Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Stripe Customer</TableHead>
                <TableHead>Subscriptions</TableHead>
                <TableHead>Last Synced</TableHead>
                <TableHead>Manual Subscription ID</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No users found matching the current filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                        <div className="text-xs text-muted-foreground font-mono">{user.user_id}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.stripe_customer_id ? (
                        <a
                          href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs text-primary hover:underline flex items-center gap-1 w-fit"
                        >
                          <Badge variant="outline" className="font-mono text-xs">
                            {user.stripe_customer_id}
                          </Badge>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <Badge variant="destructive">No Customer</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {user.subscription_count > 0 ? (
                          <>
                            <Badge variant="default" className="gap-1 bg-green-600 w-fit">
                              <CheckCircle className="h-3 w-3" />
                              {user.subscription_count} - {user.subscription_status}
                            </Badge>
                            {user.product_ids && user.product_ids.length > 0 && (
                              <div className="text-xs text-muted-foreground font-mono">
                                {user.product_ids.map(pid => pid.substring(0, 20)).join(', ')}
                              </div>
                            )}
                          </>
                        ) : (
                          <Badge variant="secondary">0</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.last_synced_at ? (
                        <span className="text-sm text-muted-foreground">
                          {new Date(user.last_synced_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="sub_xxxxxxxxxxxxx"
                        value={manualSubIds[user.user_id] || ''}
                        onChange={(e) => setManualSubIds(prev => ({
                          ...prev,
                          [user.user_id]: e.target.value
                        }))}
                        className="font-mono text-sm"
                        disabled={syncing === user.user_id}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSyncHelpOpen(true)}
                        >
                          <HelpCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRegularSync(user.user_id)}
                          disabled={syncing === user.user_id}
                        >
                          {syncing === user.user_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Sync'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setManualSyncHelpOpen(true)}
                        >
                          <HelpCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleManualSync(user.user_id)}
                          disabled={!manualSubIds[user.user_id] || syncing === user.user_id}
                        >
                          {syncing === user.user_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Manual Sync'
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
    </>
  )
}
